import QueryDatabase from "./QueryDatabase";
/*
 * sets ingredients to:
 * Ingredient_Date:date
 * Ingredient_Frozen:date or null
 * Ingredient_ID:unique int 
 * Ingredient_Name:string
 * Ingredient_Quantity:int
 * Plan_Date:date or null
 * Plan_ID:int or null, link to plan data
 * Recipe_ID:int or null, link to recipe data
 * Recipe_Ingredient_ID:int or null, link to recipe data
 * Recipe_Name:string or null
 */
export const getIngredientsData = async (serverProps, userID, ingredients, setIngredients, setServerAccess, startIndex = 0, amount = 200) => {
    setServerAccess(0);
    await QueryDatabase({
        user: userID,
        table: "ingredients",
        method: 2,
        constraints: {
            start: startIndex,
            amount: amount
        },
        filter: "",
        data: "",
        serverName: serverProps.DatabaseServer,
        serverPort: serverProps.DatabasePort
    }).then((data) => {
        if (data === -1)
            setServerAccess(-1)
        else {
            const newIngredientsArray = data.map(datum => {
                return {
                    ...datum,
                    Ingredient_Date: new Date(Date.parse(datum.Ingredient_Date)),//for consistency we want to make sure that the dates are actual date objects
                    Ingredient_Frozen: new Date(Date.parse(datum.Ingredient_Frozen)),
                    Plan_Date: new Date(Date.parse(datum.Plan_Date))
                };
            })
            setIngredients([...ingredients, ...newIngredientsArray]);
            setServerAccess(1);
        }
    });
}

/*Sends server query to delete ingredient from database, then updates the altered ingredients and plans data in cache*/
export const deleteIngredient = async (serverProps, userID, ingredients, setIngredients, plans, setPlans, ingredient) => {
    await QueryDatabase({
        user: userID,
        table: "ingredients",
        method: 1,
        filter: "",
        data: ingredient.Ingredient_ID,
        serverName: serverProps.DatabaseServer,
        serverPort: serverProps.DatabasePort
    }).then((data) => {
        if (data.affectedRows === 1) {
            const newIngredientsArray = ingredients.filter(element => {//filter out the deleted element
                return element.Ingredient_ID !== ingredient.Ingredient_ID
            })
            setIngredients(newIngredientsArray)
            const newPlans = plans.map((plan) => {
                if (plan.Plan_ID === ingredient.Plan_ID) {//find the affected plan
                    return {
                        ...plan,
                        Recipe_Ingredients: plan.Recipe_Ingredients.map((recipeIngredient) => {
                            if (recipeIngredient.Ingredient_ID === ingredient.Ingredient_ID) {//find the affected ingredient
                                return {
                                    ...recipeIngredient,
                                    Ingredient_ID: null//set Ingredient_ID to null
                                };
                            }
                            else return recipeIngredient;
                        })
                    }
                }
                else
                    return plan;
            })
            setPlans(newPlans)
        }
    })
}

/*Sends changed ingredient to database in an update query, then updates the ingredient in the cached data*/
export const updateIngredient = async (serverProps, userID, ingredients, setIngredients, ingredient) => {
    await QueryDatabase({
        user: userID,
        table: "ingredients",
        method: 5,
        filter: "",
        data: ingredient,
        serverName: serverProps.DatabaseServer,
        serverPort: serverProps.DatabasePort
    }).then((data) => {
        if (data.affectedRows === 1) {
            const newIngredients = ingredients.map(element => {
                if (element.Ingredient_ID !== ingredient.Ingredient_ID)//if the element's ID isn't the input ingredient's then no change
                    return element;
                else
                    return {
                        ...element,
                        Ingredient_Date:new Date(ingredient.Ingredient_Date)
                    }
            })
            setIngredients(newIngredients.sort((first, second) => (
                isNaN(first.Ingredient_Date) ? 0 : first.Ingredient_Date) - (isNaN(second.Ingredient_Date) ? 0 : second.Ingredient_Date) ||
                first.Ingredient_ID - second.Ingredient_ID)
            )//sort by date (NaN first), then ingredient id
        }
    })
}

/*Sends ingredient to be added to the database server, then updates the ingredient list in the cache*/
export const addIngredient = async (serverProps, userID, ingredients, setIngredients, ingredient) => {
    await QueryDatabase({
        user: userID,
        table: "ingredients",
        method: 0,
        filter: "",
        data: ingredient,
        serverName: serverProps.DatabaseServer,
        serverPort: serverProps.DatabasePort
    }).then((data) => {
        if (data.insertId) {
            const newIngredients = [...ingredients, {
                ...ingredient,
                Ingredient_Date: new Date(ingredient.Ingredient_Date),
                Ingredient_ID: data.insertId//we need the insert id from the database call, everything else should be present in the ingredient input
            }]
            setIngredients(newIngredients.sort((first, second) => (
                isNaN(first.Ingredient_Date) ? 0 : first.Ingredient_Date) - (isNaN(second.Ingredient_Date) ? 0 : second.Ingredient_Date) ||
                first.Ingredient_ID - second.Ingredient_ID)
            )
        }
    });
}

/*Sends the removed ingredient reservation data to the database server, then removes ingredient information from cached plan data and removes plan information from the ingredient data*/
export const cancelIngredientReservation = async (serverProps, userID, ingredients, setIngredients, plans, setPlans, cancelledIngredient) => {
    const data = {
        Cancelled_Ingredient: cancelledIngredient
    }
    
    if (cancelledIngredient.Ingredient_Source)//check to find if we're moving the ingredient back to its source or just removing its plan data
        data.Source_Ingredient = ingredients.find((ingredient) => {
            return (ingredient.Ingredient_ID === cancelledIngredient.Ingredient_Source//get the correct source
                && ingredient.Ingredient_Name === cancelledIngredient.Ingredient_Name//check its ingredient name and
                && ingredient.Ingredient_Date.getTime() === cancelledIngredient.Ingredient_Date.getTime())//its ingredient date haven't been changed (if they have I count it as new ingredient instance)
        })//and then set the found ingredient to data.Ingredient_Source, which we need to go into the database query
                
    await QueryDatabase({
        user: userID,
        table: "ingredients",
        method: 5,
        filter: "",
        data: data,
        serverName: serverProps.DatabaseServer,
        serverPort: serverProps.DatabasePort
    }).then((result) => {
        if (result.affectedRows === 1) {
            /* find the affected plan, find the affected Recipe_Ingredient, then set its Ingredient_ID to null*/
            const newPlans = plans.map((plan) => {
                if (plan.Plan_ID !== cancelledIngredient.Plan_ID)
                    return plan;
                else
                    return {
                        ...plan,
                        Recipe_Ingredients: plan.Recipe_Ingredients.map((recipeIngredient) => {
                            if (recipeIngredient.Recipe_Ingredient_ID !== cancelledIngredient.Recipe_Ingredient_ID)
                                return recipeIngredient;
                            else
                                return {
                                    ...recipeIngredient,
                                    Ingredient_ID:null
                                }
                        })
                    }
            })
            setPlans(newPlans);
            let newIngredients = []
            /* if we're reverting the cancelled ingredient into its source, we can just add the quantity back in, then remove the ingredient*/
            if (data.Source_Ingredient) {
                const filteredIngredients = ingredients.filter((ingredient) => {
                    return ingredient.Ingredient_ID !== cancelledIngredient.Ingredient_ID;
                })
                newIngredients = filteredIngredients.map((ingredient) => {
                    if (ingredient.Ingredient_ID === cancelledIngredient.Ingredient_Source)
                        return {
                            ...ingredient,
                            Ingredient_Quantity: ingredient.Ingredient_Quantity + cancelledIngredient.Ingredient_Quantity
                        }
                    else return ingredient;
                })
            }
            /*Otherwise, we have to change the ingredient's plan data to remove the info*/
            else {
                newIngredients = ingredients.map((ingredient) => {
                    if (ingredient.Ingredient_ID === cancelledIngredient.Ingredient_ID)
                        return {
                            ...ingredient,
                            Plan_ID: null,
                            Ingredient_Source: null,//might as well nullify the ingredient's source while we're at it, as the ingredient is no longer connected to it
                            Recipe_Ingredient_ID: null,
                            Plan_Date: null,
                            Recipe_Name: null,
                            Recipe_ID: null
                        }
                    else return ingredient;
                })
            }
            setIngredients(newIngredients)
        }
    })
}